import { useCallback, useEffect, useState } from 'react';
import { Download, Users } from 'lucide-react';
import { WorkshopV2 } from '../../../interfaces/workshop.interface';
import { Participant } from '../../../interfaces/reservation.interface';
import {
  fetchParticipants,
  participantRevenue,
  reservationPlaces,
} from '../../../utils/reservation';
import {
  getBookingModeLabel,
  getTypeLabel,
} from '../../../utils/workshopMigration';
import { sanitizeHtml } from '../../../utils/sanitizeHtml';
import { exportWorkshopParticipants } from '../utils/exportParticipants';

interface Props {
  workshop: WorkshopV2;
  /** Déjà chargés par la page ; `undefined` = à récupérer nous-mêmes. */
  participants?: Participant[];
  onClose: () => void;
}

const Meta = ({ label, value }: { label: string; value: string }) => (
  <div>
    <p className="text-xs uppercase tracking-wide text-gray-400">{label}</p>
    <p className="text-sm text-gray-700">{value}</p>
  </div>
);

const WorkshopHistoryPopup = ({ workshop, participants, onClose }: Props) => {
  const [rows, setRows] = useState<Participant[]>(participants || []);
  const [loading, setLoading] = useState(participants === undefined);
  const [error, setError] = useState<string | null>(null);

  const loadParticipants = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await fetchParticipants(workshop.id));
    } catch (err) {
      console.error('Erreur chargement participants', err);
      setError(
        String(err).includes('Missing or insufficient permissions')
          ? 'Accès refusé. Vérifiez vos règles Firestore / rôle admin.'
          : 'Impossible de charger les participants pour cet atelier.'
      );
    } finally {
      setLoading(false);
    }
  }, [workshop.id]);

  useEffect(() => {
    if (participants === undefined) loadParticipants();
    else setRows(participants);
  }, [participants, loadParticipants]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const date = workshop.date.toDate().toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  const places = rows.reduce((sum, p) => sum + reservationPlaces(p), 0);
  const revenue = rows.reduce(
    (sum, p) =>
      sum + participantRevenue(p, workshop.priceSolo, workshop.priceDuo),
    0
  );
  const capacity = workshop.maxParticipants || 0;

  const prices = [
    workshop.priceSolo > 0 ? `Solo ${workshop.priceSolo.toFixed(2)} €` : null,
    workshop.priceDuo > 0 ? `Duo ${workshop.priceDuo.toFixed(2)} €` : null,
  ]
    .filter(Boolean)
    .join(' — ');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col rounded-2xl bg-white shadow-xl border border-[#623413]/10">
        <div className="flex items-start justify-between gap-4 border-b border-neutral-200 px-6 py-4">
          <div className="flex items-center gap-3 min-w-0">
            <img
              src={workshop.imageUrl || '/defaultimages/defaultcake.png'}
              alt={workshop.title}
              className="h-12 w-12 shrink-0 rounded object-cover border border-[#623413]/20"
            />
            <div className="min-w-0">
              <h2 className="truncate text-lg font-semibold text-[#623413]">
                {workshop.title}
              </h2>
              <p className="text-sm text-gray-500 first-letter:uppercase">
                {date}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="shrink-0 rounded bg-neutral-100 px-3 py-1.5 text-sm text-neutral-700 hover:bg-neutral-200"
          >
            Fermer
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <Meta label="Lieu" value={workshop.location || '—'} />
            <Meta label="Durée" value={`${workshop.duration} min`} />
            <Meta label="Type" value={getTypeLabel(workshop.type)} />
            <Meta
              label="Mode"
              value={getBookingModeLabel(workshop.bookingMode)}
            />
            <Meta label="Tarifs" value={prices || '—'} />
            <Meta
              label="Places occupées"
              value={capacity > 0 ? `${places} / ${capacity}` : String(places)}
            />
            <Meta label="Réservations" value={String(rows.length)} />
            <Meta
              label="Recettes estimées"
              value={revenue.toLocaleString('fr-FR', {
                style: 'currency',
                currency: 'EUR',
                maximumFractionDigits: 0,
              })}
            />
          </div>

          {workshop.description?.trim() && (
            <div className="mt-5">
              <p className="mb-1 text-xs uppercase tracking-wide text-gray-400">
                Description
              </p>
              <div
                className="prose prose-sm prose-neutral max-h-32 max-w-none overflow-y-auto rounded border bg-gray-50 p-3"
                dangerouslySetInnerHTML={{
                  __html: String(sanitizeHtml(workshop.description)),
                }}
              />
            </div>
          )}

          <div className="mt-6 flex items-center justify-between gap-3">
            <h3 className="flex items-center gap-2 font-semibold text-[#623413]">
              <Users size={18} />
              Participants
              {!loading && !error && (
                <span className="text-sm font-normal text-gray-400">
                  ({rows.length})
                </span>
              )}
            </h3>
            {!loading && !error && rows.length > 0 && (
              <button
                type="button"
                onClick={() => exportWorkshopParticipants(workshop, rows)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-xs text-gray-700 transition hover:bg-gray-50"
                title="Exporter la liste au format CSV"
              >
                <Download size={14} />
                Exporter
              </button>
            )}
          </div>

          <div className="mt-3">
            {loading && <p className="text-sm text-neutral-600">Chargement…</p>}
            {error && <p className="text-sm text-[#BB0F33]">{error}</p>}
            {!loading && !error && rows.length === 0 && (
              <p className="text-sm text-neutral-600">
                Aucune réservation enregistrée pour cet atelier.
              </p>
            )}

            {!loading && !error && rows.length > 0 && (
              <div className="overflow-x-auto">
                <table className="min-w-full border-collapse">
                  <thead>
                    <tr className="border-b text-left text-sm text-neutral-600">
                      <th className="py-2 pr-4">Nom</th>
                      <th className="py-2 pr-4">Email</th>
                      <th className="py-2 pr-4">Téléphone</th>
                      <th className="py-2 pr-4">Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((p) => (
                      <tr
                        key={p.id}
                        className="border-b text-sm last:border-b-0"
                      >
                        <td className="py-2 pr-4">{p.displayName || '—'}</td>
                        <td className="py-2 pr-4 break-all">
                          {p.email || '—'}
                        </td>
                        <td className="py-2 pr-4">{p.phone || '—'}</td>
                        <td className="py-2 pr-4">
                          <span
                            className={`rounded px-2 py-0.5 text-xs ${
                              p.type === 'duo'
                                ? 'bg-purple-100 text-purple-700'
                                : 'bg-blue-100 text-blue-700'
                            }`}
                          >
                            {p.type === 'duo' ? 'Duo' : 'Solo'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkshopHistoryPopup;
